import { supabase } from './supabaseClient';
import {
  Client,
  Workplace,
  PsagotTeamMember,
  Sector,
  Company,
  ClientInterest,
  ClientInterestWithDetails,
  ClientActivity,
  NotificationStats,
  DashboardStats,
  CreateClientRequest,
  UpdateClientRequest,
  AddClientInterestRequest,
  RemoveClientInterestRequest,
  CreateWorkplaceRequest,
  UpdateWorkplaceRequest
} from '../types/client';

export class ClientService {
  // Client operations
  static async getClients(): Promise<Client[]> {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        workplace:workplaces(name, address, city, country),
        psagot_team_member:psagot_team(first_name, last_name, email, position)
      `)
      .order('first_name');

    if (error) throw error;
    return data || [];
  }

  static async getClient(id: number): Promise<Client | null> {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        workplace:workplaces(name, address, city, country),
        psagot_team_member:psagot_team(first_name, last_name, email, position)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  static async createClient(clientData: CreateClientRequest): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .insert([clientData])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateClient(id: number, clientData: UpdateClientRequest): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .update(clientData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteClient(id: number): Promise<void> {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Workplace operations
  static async getWorkplaces(): Promise<Workplace[]> {
    const { data, error } = await supabase
      .from('workplaces')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  }

  static async createWorkplace(workplaceData: CreateWorkplaceRequest): Promise<Workplace> {
    const { data, error } = await supabase
      .from('workplaces')
      .insert([workplaceData])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateWorkplace(id: number, workplaceData: UpdateWorkplaceRequest): Promise<Workplace> {
    const { data, error } = await supabase
      .from('workplaces')
      .update(workplaceData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteWorkplace(id: number): Promise<void> {
    const { error } = await supabase
      .from('workplaces')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Psagot Team operations
  static async getPsagotTeam(): Promise<PsagotTeamMember[]> {
    const { data, error } = await supabase
      .from('psagot_team')
      .select('*')
      .eq('is_active', true)
      .order('first_name');

    if (error) throw error;
    return data || [];
  }

  // Sector operations
  static async getSectors(): Promise<Sector[]> {
    const { data, error } = await supabase
      .from('sectors')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  }

  // Company operations
  static async getCompanies(): Promise<Company[]> {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  }

  // Client Interest operations
  static async getClientInterests(clientId: number): Promise<ClientInterestWithDetails[]> {
    const { data, error } = await supabase
      .from('client_interests')
      .select(`
        *,
        client:clients(first_name, last_name, email),
        company:companies(name, ticker)
      `)
      .eq('client_id', clientId);

    if (error) throw error;
    return data || [];
  }

  static async addClientInterest(interestData: AddClientInterestRequest): Promise<ClientInterest> {
    const { data, error } = await supabase
      .from('client_interests')
      .insert([interestData])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async removeClientInterest(interestData: RemoveClientInterestRequest): Promise<void> {
    const { error } = await supabase
      .from('client_interests')
      .delete()
      .eq('client_id', interestData.client_id)
      .eq('sector_id', interestData.sector_id || null)
      .eq('company_id', interestData.company_id || null);

    if (error) throw error;
  }

  // ── Notification toggle on interests ─────────────────────────────────

  static async updateInterestNotify(interestId: number, updates: {
    auto_notify?: boolean;
    notify_whatsapp?: boolean;
    notify_email?: boolean;
    notify_sms?: boolean;
  }): Promise<void> {
    const { error } = await supabase
      .from('client_interests')
      .update(updates)
      .eq('id', interestId);

    if (error) throw error;
  }

  // ── Activity log ────────────────────────────────────────────────────

  static async getClientActivity(clientId: number, limit: number = 50): Promise<ClientActivity[]> {
    const { data, error } = await supabase
      .from('client_activity_log')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  // ── Dashboard stats ─────────────────────────────────────────────────

  static async getDashboardStats(): Promise<DashboardStats> {
    // Total clients
    const { count: totalClients, error: e1 } = await supabase
      .from('clients')
      .select('*', { count: 'exact', head: true });
    if (e1) throw e1;

    // Active alerts: clients with agent_enabled AND at least one auto_notify interest
    const { data: alertData } = await supabase
      .from('client_interests')
      .select('client_id, client:clients!inner(agent_enabled)')
      .eq('auto_notify', true)
      .eq('client:clients.agent_enabled', true);
    const activeAlerts = alertData ? new Set(alertData.map(r => r.client_id)).size : 0;

    // Docs sent this week
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const { count: docsSent, error: e3 } = await supabase
      .from('notification_log')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'sent')
      .gte('created_at', weekAgo.toISOString());
    if (e3) throw e3;

    // Top sectors by client count
    const { data: sectorData, error: e4 } = await supabase
      .from('client_interests')
      .select('sector_name');
    if (e4) throw e4;

    const sectorCounts: Record<string, number> = {};
    for (const row of (sectorData || [])) {
      const name = row.sector_name;
      if (name) {
        sectorCounts[name] = (sectorCounts[name] || 0) + 1;
      }
    }
    const topSectors = Object.entries(sectorCounts)
      .map(([name, client_count]) => ({ name, client_count }))
      .sort((a, b) => b.client_count - a.client_count)
      .slice(0, 10);

    return {
      total_clients: totalClients || 0,
      active_alerts: activeAlerts,
      docs_sent_this_week: docsSent || 0,
      top_sectors: topSectors,
    };
  }

  static async getNotificationStats(days: number = 30): Promise<NotificationStats[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);

    const { data, error } = await supabase
      .from('notification_log')
      .select('channel, status, created_at')
      .gte('created_at', since.toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Group by day + channel + status
    const grouped: Record<string, NotificationStats> = {};
    for (const row of (data || [])) {
      const day = row.created_at.substring(0, 10);
      const key = `${day}_${row.channel}_${row.status}`;
      if (!grouped[key]) {
        grouped[key] = { day, channel: row.channel, status: row.status, count: 0 };
      }
      grouped[key].count++;
    }

    return Object.values(grouped).sort((a, b) => b.day.localeCompare(a.day));
  }

  static async getRecentNotifications(limit: number = 20): Promise<any[]> {
    const { data, error } = await supabase
      .from('notification_log')
      .select(`
        *,
        client:clients(first_name, last_name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  // ── Bulk actions ────────────────────────────────────────────────────

  static async bulkAddInterest(
    clientIds: number[],
    interest: { sector_id?: number; company_id?: number; sector_name?: string }
  ): Promise<void> {
    const rows = clientIds.map(client_id => ({
      client_id,
      ...interest,
    }));
    const { error } = await supabase
      .from('client_interests')
      .upsert(rows, { onConflict: 'client_id,sector_id,company_id' });

    if (error) throw error;
  }

  static async bulkToggleNotify(sectorName: string, enabled: boolean): Promise<void> {
    const { error } = await supabase
      .from('client_interests')
      .update({ auto_notify: enabled })
      .eq('sector_name', sectorName);

    if (error) throw error;
  }

  // ── Client notes ────────────────────────────────────────────────────

  static async updateClientNotes(clientId: number, notes: string): Promise<void> {
    const { error } = await supabase
      .from('clients')
      .update({ notes })
      .eq('id', clientId);

    if (error) throw error;
  }

  // Search operations
  static async searchClients(query: string): Promise<Client[]> {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        workplace:workplaces(name, address, city, country),
        psagot_team_member:psagot_team(first_name, last_name, email, position)
      `)
      .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%`)
      .order('first_name');

    if (error) throw error;
    return data || [];
  }

  static async searchWorkplaces(query: string): Promise<Workplace[]> {
    const { data, error } = await supabase
      .from('workplaces')
      .select('*')
      .or(`name.ilike.%${query}%,city.ilike.%${query}%`)
      .order('name');

    if (error) throw error;
    return data || [];
  }
}
